import { defineStore } from 'pinia'
import { computed, readonly, ref, watch } from 'vue'

import type { InDmChatItem } from '@/client/components/_molecules/in_dm_chat_row'
import type { InDmPlaybackControl } from '@/client/components/_organisms/in_dm_chat_area'
import type { NodeContainerData, NodeResponse } from '@/typings/api/instagram_instantwin_node_container'

import { getAllInstagramScenarioContainers, PreviewScenarioData } from '../api/getAllInstagramScenarioContainers'
import { buildPreviewChatItems, PreviewPattern } from '../utils/buildPreviewChatItems'
import { PREVIEW_STEP_INTERVAL_MS } from '../utils/previewConstants'

export type PreviewPlaybackState = 'before' | 'playing' | 'paused' | 'done'

type PreviewSelectNode = Extract<NodeResponse, { type: 'SELECT' }>

const initialPlaybackControl = (): InDmPlaybackControl => ({
  stepIntervalMs: PREVIEW_STEP_INTERVAL_MS,
  isPlaying: false,
  isReset: false
})

export const useInstagramScenarioPreviewStore = defineStore('instagramScenarioPreview', () => {
  // State
  const prizeId = ref<number | null>(null)
  const account = ref<PreviewScenarioData['account'] | null>(null)
  const containers = ref<NodeContainerData[]>([])
  const loading = ref(false)
  const pattern = ref<PreviewPattern>('win')
  const answers = ref<Record<number, number>>({})
  const playbackControl = ref<InDmPlaybackControl>(initialPlaybackControl())
  const visibleCount = ref(0)
  const hasStarted = ref(false)

  // Computed
  const chatItems = computed<InDmChatItem[]>(() =>
    buildPreviewChatItems({ containers: containers.value, pattern: pattern.value, answers: answers.value })
  )

  const selectNodes = computed<PreviewSelectNode[]>(() => {
    if (pattern.value === 'unfollow' || pattern.value === 'missing') return []
    const freeContainer = containers.value.find(container => container.type === 'FREE')
    if (!freeContainer) return []
    return freeContainer.nodes.filter((node): node is PreviewSelectNode => node.type === 'SELECT')
  })

  const canShowUnfollow = computed<boolean>(() => {
    const followCheck = containers.value.find(container => container.type === 'FOLLOW_CHECK')
    return Boolean(followCheck?.nodes.some(node => node.type === 'SELECT'))
  })

  const canShowMissing = computed<boolean>(() => {
    if (canShowUnfollow.value) return true
    const free = containers.value.find(container => container.type === 'FREE')
    return Boolean(free?.nodes.some(node => node.type === 'SELECT'))
  })

  const playbackState = computed<PreviewPlaybackState>(() => {
    if (playbackControl.value.isPlaying) return 'playing'
    if (!hasStarted.value) return 'before'
    const total = chatItems.value.length
    if (total > 0 && visibleCount.value >= total) return 'done'
    return 'paused'
  })

  const clearPlayback = (): void => {
    visibleCount.value = 0
    hasStarted.value = false
    playbackControl.value = initialPlaybackControl()
  }

  // Actions
  const open = async (id: number): Promise<void> => {
    if (loading.value) return
    prizeId.value = id
    loading.value = true
    try {
      const data = await getAllInstagramScenarioContainers(id)
      // 取得中に close() / 別 prizeId の open() が起きた場合は古いレスポンスを破棄する
      if (prizeId.value !== id) return
      account.value = data.account
      containers.value = data.containers
    } finally {
      if (prizeId.value === id) {
        loading.value = false
      }
    }
  }

  const close = (): void => {
    prizeId.value = null
    account.value = null
    containers.value = []
    loading.value = false
    pattern.value = 'win'
    answers.value = {}
    clearPlayback()
  }

  const setPattern = (next: PreviewPattern): void => {
    pattern.value = next
    clearPlayback()
  }

  const setAnswer = (selectNodeId: number, optionId: number): void => {
    answers.value = { ...answers.value, [selectNodeId]: optionId }
    clearPlayback()
  }

  const play = (): void => {
    hasStarted.value = true
    playbackControl.value = { ...playbackControl.value, isPlaying: true, isReset: false }
  }

  const stop = (): void => {
    playbackControl.value = { ...playbackControl.value, isPlaying: false }
  }

  const restart = (): void => {
    hasStarted.value = true
    playbackControl.value = { ...playbackControl.value, isPlaying: true, isReset: true }
  }

  const syncVisibleCount = (count: number): void => {
    visibleCount.value = count
    const total = chatItems.value.length
    if (total > 0 && count >= total && playbackControl.value.isPlaying) {
      playbackControl.value = { ...playbackControl.value, isPlaying: false }
    }
  }

  const syncPlaybackControl = (control: InDmPlaybackControl): void => {
    playbackControl.value = control
  }

  const setStepIntervalMs = (ms: number): void => {
    playbackControl.value = { ...playbackControl.value, stepIntervalMs: ms }
  }

  // sync flush: シナリオ取得直後の同期的な整合性が欲しく、後段の chatItems 算出にも即時反映させたいため
  // 個別 watch にする理由: Vue 2.7 composition の array source + sync flush 組合せでの取りこぼしを避ける
  watch(
    canShowUnfollow,
    next => {
      if (!next && pattern.value === 'unfollow') {
        pattern.value = 'win'
        clearPlayback()
      }
    },
    { flush: 'sync' }
  )
  watch(
    canShowMissing,
    next => {
      if (!next && pattern.value === 'missing') {
        pattern.value = 'win'
        clearPlayback()
      }
    },
    { flush: 'sync' }
  )

  return {
    prizeId: readonly(prizeId),
    account: readonly(account),
    containers: readonly(containers),
    loading: readonly(loading),
    pattern: readonly(pattern),
    answers: readonly(answers),
    playbackControl: readonly(playbackControl),
    visibleCount: readonly(visibleCount),
    hasStarted: readonly(hasStarted),

    // Computed
    chatItems,
    selectNodes,
    canShowUnfollow,
    canShowMissing,
    playbackState,

    // Actions
    open,
    close,
    setPattern,
    setAnswer,
    play,
    stop,
    restart,
    syncVisibleCount,
    syncPlaybackControl,
    setStepIntervalMs
  }
})
